import React from 'react';

import { selectExternalInfos } from '@/domain/external/external.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { ReadOnlyForm } from '@/ui/pages/external/components/ReadOnlyForm';
import { Step1Start } from '@/ui/pages/external/components/Step1Start';
import { Step2Form } from '@/ui/pages/external/components/Step2Form';
import { Step3Done } from '@/ui/pages/external/components/Step3Done';
import { useExternalInvitationUpdate } from '@/ui/pages/external/hooks/useExternalInvitationUpdate';
import { useExternalSteps } from '@/ui/pages/external/hooks/useExternalSteps';

export type FormComponent = React.ReactElement<{ readOnly: boolean }>;

export function GenericExternalPage({
  welcomeText,
  children,
}: {
  welcomeText: string;
  children: FormComponent;
}) {
  const { step, setStep } = useExternalSteps();
  const { changeInvitationStatusToSubmit } = useExternalInvitationUpdate();
  const { isValidated } = useAppSelector(selectExternalInfos);

  const onClickStartForm = () => {
    setStep('form');
  };

  const onClickValidate = () => {
    changeInvitationStatusToSubmit();
    setStep('done');
  };

  if (isValidated) {
    return <ReadOnlyForm>{addReadOnlyProp(children)}</ReadOnlyForm>;
  }

  switch (step) {
    case 'start':
      return (
        <Step1Start
          welcomeText={welcomeText}
          onClickStartForm={onClickStartForm}
        />
      );
    case 'form':
      return (
        <Step2Form onClickValidate={onClickValidate}>{children}</Step2Form>
      );
    case 'done':
      return <Step3Done />;
    default:
      return '';
  }
}

function addReadOnlyProp(children: FormComponent) {
  return React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { readOnly: true });
    }
    return child; // If not a valid React element, return it as is
  });
}
